import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Users, Grid3x3, Trophy, ScrollText, Radio, Eye, EyeOff,
  Lock, Unlock, Plus, Pencil, UserMinus, UserPlus, Download, Sparkles, X, CheckCircle2, Clock,
} from "lucide-react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { toast } from "sonner";

const JUDGE_ORDER = ["Crunch Cervantes", "Carmie De Leon", "Apple Esplana-Manansala", "Michael Carlos"];

const TABS = [
  { id: "stage", label: "Stage", icon: Radio },
  { id: "tally", label: "Tally", icon: LayoutDashboard },
  { id: "matrix", label: "Matrix", icon: Grid3x3 },
  { id: "results", label: "Results", icon: Trophy },
  { id: "contestants", label: "Contestants", icon: Users },
  { id: "audit", label: "Audit", icon: ScrollText },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("stage");
  const [dash, setDash] = useState(null);
  const [contestants, setContestants] = useState([]);
  const [scores, setScores] = useState([]);
  const [results, setResults] = useState(null);
  const [audit, setAudit] = useState([]);

  const load = useCallback(async () => {
    const [d, c, s, r, a] = await Promise.all([
      api.get("/dashboard"),
      api.get("/contestants"),
      api.get("/scores/all"),
      api.get("/results"),
      api.get("/audit"),
    ]);
    setDash(d.data);
    setContestants(c.data);
    setScores(s.data);
    setResults(r.data);
    setAudit(a.data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useRealtime(() => load());

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4">
      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 h-11 rounded-xl whitespace-nowrap font-semibold text-sm border transition-colors ${
                active ? "bg-amber-500 text-slate-950 border-amber-500" : "bg-slate-900 text-slate-300 border-slate-700 hover:border-amber-500/40"
              }`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "stage" && <StageControl dash={dash} contestants={contestants} reload={load} />}
      {tab === "tally" && <TallyView dash={dash} />}
      {tab === "matrix" && <MatrixView contestants={contestants} scores={scores} reload={load} />}
      {tab === "results" && <ResultsView results={results} state={dash?.state} reload={load} />}
      {tab === "contestants" && <ContestantsView contestants={contestants} reload={load} />}
      {tab === "audit" && <AuditView audit={audit} />}
    </div>
  );
}

/* ---------------- Stage Control ---------------- */
function StageControl({ dash, contestants, reload }) {
  if (!dash) return null;
  const state = dash.state;
  const active = contestants.filter((c) => c.status === "active");

  const update = async (patch) => {
    try {
      await api.put("/event/state", patch);
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <div className="card-kwc p-5">
          <h3 className="font-display font-bold text-lg text-amber-200 mb-4">Current Performer</h3>
          <label className="block text-sm text-slate-400 mb-2">Select contestant</label>
          <select
            data-testid="current-contestant-select"
            value={state.current_contestant_id || ""}
            onChange={(e) => update({ current_contestant_id: e.target.value })}
            className="w-full h-14 px-4 rounded-xl bg-slate-950 border border-slate-700 text-slate-100 focus:border-amber-500 focus:outline-none text-lg mb-5"
          >
            <option value="">— None —</option>
            {active.map((c) => (
              <option key={c.id} value={c.id}>#{c.number} · {c.name}</option>
            ))}
          </select>

          <label className="block text-sm text-slate-400 mb-2">Active Song</label>
          <div className="grid grid-cols-2 gap-3 mb-5">
            {[1, 2].map((s) => (
              <button key={s} data-testid={`song-tab-${s}`} onClick={() => update({ current_song: s })}
                className={`h-14 rounded-xl font-display font-bold text-lg border transition-colors ${
                  state.current_song === s ? "bg-amber-500 text-slate-950 border-amber-500" : "bg-slate-900 text-slate-300 border-slate-700"
                }`}>
                Song {s}
              </button>
            ))}
          </div>

          <label className="block text-sm text-slate-400 mb-2">Scoring Status</label>
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="admin-open-scoring-button" onClick={() => update({ scoring_open: true })}
              className={`h-14 rounded-xl font-bold border transition-colors ${
                state.scoring_open ? "bg-emerald-500 text-slate-950 border-emerald-500" : "bg-slate-900 text-emerald-300 border-emerald-500/40"
              }`}>
              Open Scoring
            </button>
            <button data-testid="admin-close-scoring-button" onClick={() => update({ scoring_open: false })}
              className={`h-14 rounded-xl font-bold border transition-colors ${
                !state.scoring_open ? "bg-red-500 text-slate-950 border-red-500" : "bg-slate-900 text-red-300 border-red-500/40"
              }`}>
              Close Scoring
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="card-kwc p-5">
          <h3 className="font-display font-bold text-lg text-amber-200 mb-4">Judge Submissions</h3>
          {dash.current_contestant ? (
            <>
              <p className="text-xs text-slate-400 mb-3">
                {dash.current_contestant.name} · Song {state.current_song}
              </p>
              <div className="space-y-2">
                {dash.judges.sort((a, b) => JUDGE_ORDER.indexOf(a.name) - JUDGE_ORDER.indexOf(b.name)).map((j) => {
                  const done = dash.submitted_judges.includes(j.name);
                  return (
                    <div key={j.id} data-testid={`judge-status-${j.name.split(" ")[0].toLowerCase()}`}
                      className={`flex items-center justify-between px-3 h-12 rounded-xl border ${
                        done ? "bg-emerald-500/10 border-emerald-500/30" : "bg-slate-950 border-slate-700"
                      }`}>
                      <span className="text-sm text-slate-200">{j.name}</span>
                      {done ? (
                        <span className="flex items-center gap-1 text-emerald-300 text-xs font-semibold"><CheckCircle2 className="w-4 h-4" /> Submitted</span>
                      ) : (
                        <span className="flex items-center gap-1 text-slate-400 text-xs"><Clock className="w-4 h-4" /> Pending</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-slate-500 text-sm">No performer selected.</p>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Stat label="Total" value={dash.contestants_total} />
          <Stat label="Done" value={dash.contestants_completed} accent="emerald" />
          <Stat label="Left" value={dash.contestants_remaining} />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const color = accent === "emerald" ? "text-emerald-400" : "text-amber-400";
  return (
    <div className="card-kwc p-4 text-center">
      <div className={`font-score font-black text-3xl ${color}`}>{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}

/* ---------------- Tally ---------------- */
function TallyView({ dash }) {
  if (!dash) return null;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Contestants" value={dash.contestants_total} />
        <Stat label="Completed" value={dash.contestants_completed} accent="emerald" />
        <Stat label="Remaining" value={dash.contestants_remaining} />
        <Stat label="Submitted Now" value={dash.submitted_judges.length} accent="emerald" />
      </div>
      <div className="card-kwc p-5">
        <h3 className="font-display font-bold text-lg text-amber-200 mb-2">Current Stage</h3>
        <p className="text-slate-300">
          {dash.current_contestant ? `#${dash.current_contestant.number} · ${dash.current_contestant.name}` : "No performer selected"}
          {" — "}Song {dash.state.current_song} · {dash.state.scoring_open ? <span className="text-emerald-400">Scoring Open</span> : <span className="text-red-400">Scoring Closed</span>}
        </p>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-emerald-400 uppercase tracking-widest mb-1">Submitted</div>
            <div className="text-slate-200 text-sm">{dash.submitted_judges.join(", ") || "—"}</div>
          </div>
          <div>
            <div className="text-xs text-amber-400 uppercase tracking-widest mb-1">Pending</div>
            <div className="text-slate-200 text-sm">{dash.pending_judges.join(", ") || "—"}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Matrix ---------------- */
function MatrixView({ contestants, scores, reload }) {
  const [reveal, setReveal] = useState(false);
  const [songSel, setSongSel] = useState(1);
  const [unlockTarget, setUnlockTarget] = useState(null);

  const judges = JUDGE_ORDER;

  const cell = (cid, judgeName, song) =>
    scores.find((s) => s.contestant_id === cid && s.judge_name === judgeName && s.song === song && s.status === "submitted");

  const rowVals = (cid, song) =>
    judges.map((j) => cell(cid, j, song)).filter(Boolean).map((s) => s.total);

  const mask = (v) => (reveal ? v : "••");

  return (
    <div className="card-kwc p-4 sm:p-5">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h3 className="font-display font-bold text-lg text-amber-200">Live Scores Matrix</h3>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border border-slate-700">
            {[1, 2].map((s) => (
              <button key={s} data-testid={`matrix-song-${s}`} onClick={() => setSongSel(s)}
                className={`px-3 h-9 text-sm font-semibold ${songSel === s ? "bg-amber-500 text-slate-950" : "bg-slate-900 text-slate-300"}`}>
                Song {s}
              </button>
            ))}
          </div>
          <button data-testid="toggle-score-visibility-button" onClick={() => setReveal((r) => !r)}
            className="flex items-center gap-1.5 px-3 h-9 rounded-lg bg-slate-800 border border-amber-500/30 text-amber-200 text-sm font-semibold">
            {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {reveal ? "Hide" : "Reveal"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="scores-matrix-table">
          <thead>
            <tr className="text-left text-slate-400 border-b border-slate-700">
              <th className="py-2 pr-3 sticky left-0 bg-slate-900">Contestant</th>
              {judges.map((j) => <th key={j} className="py-2 px-3 text-center">{j.split(" ")[0]}</th>)}
              <th className="py-2 px-3 text-center text-amber-400">Average</th>
            </tr>
          </thead>
          <tbody>
            {contestants.map((c) => {
              const vals = rowVals(c.id, songSel);
              const avg = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
              return (
                <tr key={c.id} className="border-b border-slate-800" data-testid={`matrix-row-${c.number}`}>
                  <td className="py-2.5 pr-3 sticky left-0 bg-slate-900">
                    <span className="text-slate-500 font-score">#{c.number}</span> <span className="text-slate-200">{c.name}</span>
                  </td>
                  {judges.map((j) => {
                    const s = cell(c.id, j, songSel);
                    return (
                      <td key={j} className="py-2.5 px-3 text-center">
                        {s ? (
                          <button data-testid={`matrix-cell-${c.number}-${j.split(" ")[0].toLowerCase()}`}
                            onClick={() => setUnlockTarget({ score: s, contestant: c, judge: j })}
                            className="inline-flex items-center gap-1 font-score font-bold text-amber-300 hover:text-amber-100">
                            {mask(s.total)} <Lock className="w-3 h-3 text-slate-500" />
                          </button>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="py-2.5 px-3 text-center font-score font-black text-amber-400">
                    {avg ? mask(avg) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {unlockTarget && (
        <UnlockModal target={unlockTarget} onClose={() => setUnlockTarget(null)} reload={reload} />
      )}
    </div>
  );
}

function UnlockModal({ target, onClose, reload }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error("Reason is required");
    setBusy(true);
    try {
      await api.post(`/scores/${target.score.id}/unlock`, { reason });
      toast.success("Score unlocked");
      await reload();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setBusy(false);
  };
  return (
    <Modal onClose={onClose} title="Unlock Score">
      <p className="text-sm text-slate-400 mb-4">
        Unlock <span className="text-amber-300">{target.judge}</span>'s score for
        {" "}<span className="text-amber-300">#{target.contestant.number} {target.contestant.name}</span> · Song {target.score.song}
        {" "}(total {target.score.total}). A reason is required and will be recorded in the audit trail.
      </p>
      <textarea data-testid="unlock-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
        placeholder="Reason for unlocking…"
        className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-slate-100 focus:border-amber-500 focus:outline-none mb-4" />
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 h-12 rounded-xl border border-slate-700 text-slate-300">Cancel</button>
        <button data-testid="confirm-unlock-button" onClick={submit} disabled={busy || !reason.trim()}
          className="flex-1 h-12 rounded-xl bg-red-500 text-slate-950 font-bold flex items-center justify-center gap-2 disabled:opacity-40">
          <Unlock className="w-4 h-4" /> Unlock
        </button>
      </div>
    </Modal>
  );
}

/* ---------------- Results ---------------- */
function ResultsView({ results, state, reload }) {
  if (!results) return null;
  const rows = results.rows;
  const ranked = [...rows].filter((r) => r.rank).sort((a, b) => a.rank - b.rank);
  const token = localStorage.getItem("kwc_token");

  const reveal = async () => {
    try {
      await api.post("/event/reveal");
      await reload();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-display font-bold text-lg text-amber-200">Results & Rankings</h3>
        <div className="flex gap-2">
          <a data-testid="export-csv-button" href={`${API}/export/scores.csv?token=${token}`}
            className="flex items-center gap-1.5 px-4 h-11 rounded-xl bg-slate-800 border border-amber-500/30 text-amber-200 text-sm font-semibold">
            <Download className="w-4 h-4" /> Export CSV
          </a>
          <button data-testid="final-results-reveal-button" onClick={reveal}
            className={`flex items-center gap-1.5 px-4 h-11 rounded-xl text-sm font-bold ${
              state?.results_revealed ? "bg-slate-800 border border-amber-500/30 text-amber-200" : "btn-gold"
            }`}>
            <Sparkles className="w-4 h-4" /> {state?.results_revealed ? "Hide Reveal" : "Reveal Final"}
          </button>
        </div>
      </div>

      {state?.results_revealed && ranked.length > 0 && (
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          className="card-kwc gold-glow p-6 border-amber-500/50" data-testid="final-podium">
          <div className="text-center mb-5">
            <div className="text-xs text-amber-400 uppercase tracking-[0.3em]">KWC Luzon 2026</div>
            <div className="font-display font-black text-3xl text-amber-100 text-glow">Final Champions</div>
          </div>
          <div className="grid grid-cols-3 gap-3 items-end">
            {[1, 0, 2].map((idx) => {
              const r = ranked[idx];
              if (!r) return <div key={idx} />;
              const heights = { 0: "h-28", 1: "h-20", 2: "h-16" };
              const medal = ["🥇", "🥈", "🥉"][r.rank - 1];
              return (
                <div key={r.contestant_id} className="text-center">
                  <div className="text-2xl mb-1">{medal}</div>
                  <div className="text-amber-100 font-semibold text-sm truncate">{r.name}</div>
                  <div className="font-score font-black text-amber-400 text-xl">{r.combined}</div>
                  <div className={`mt-2 rounded-t-lg bg-gradient-to-t from-amber-700 to-amber-400 ${heights[r.rank - 1]}`} />
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      <div className="card-kwc p-4 sm:p-5 overflow-x-auto">
        <table className="w-full text-sm" data-testid="results-table">
          <thead>
            <tr className="text-left text-slate-400 border-b border-slate-700">
              <th className="py-2 pr-3">Rank</th>
              <th className="py-2 px-3">Contestant</th>
              <th className="py-2 px-3 text-center">Song 1</th>
              <th className="py-2 px-3 text-center">Song 2</th>
              <th className="py-2 px-3 text-center text-amber-400">Combined</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.contestant_id} className={`border-b border-slate-800 ${r.status !== "active" ? "opacity-40" : ""}`}>
                <td className="py-2.5 pr-3 font-score font-bold text-amber-300">{r.rank || "—"}</td>
                <td className="py-2.5 px-3 text-slate-200">
                  <span className="text-slate-500 font-score">#{r.number}</span> {r.name}
                  {r.status !== "active" && <span className="ml-2 text-[10px] text-red-400">WITHDRAWN</span>}
                </td>
                <td className="py-2.5 px-3 text-center font-score text-slate-300">{r.song1_avg ?? "—"}</td>
                <td className="py-2.5 px-3 text-center font-score text-slate-300">{r.song2_avg ?? "—"}</td>
                <td className="py-2.5 px-3 text-center font-score font-black text-amber-400">{r.combined ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Contestants ---------------- */
function ContestantsView({ contestants, reload }) {
  const [editing, setEditing] = useState(null); // contestant or "new"

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); await reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-lg text-amber-200">Contestant Master List</h3>
        <button data-testid="add-contestant-button" onClick={() => setEditing("new")}
          className="btn-gold flex items-center gap-1.5 px-4 h-11 rounded-xl text-sm">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {contestants.map((c) => (
          <div key={c.id} className={`card-kwc p-4 ${c.status !== "active" ? "opacity-60" : ""}`} data-testid={`contestant-card-${c.number}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-xl bg-slate-800 border border-amber-500/30 flex items-center justify-center shrink-0">
                  <span className="font-score font-black text-amber-300">#{c.number}</span>
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-100 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">S1: {c.song1_title || "—"}</div>
                  <div className="text-xs text-slate-500 truncate">S2: {c.song2_title || "—"}</div>
                </div>
              </div>
              {c.status !== "active" && <span className="text-[10px] text-red-400 font-semibold">WITHDRAWN</span>}
            </div>
            <div className="flex gap-2 mt-3">
              <button data-testid={`edit-contestant-${c.number}`} onClick={() => setEditing(c)}
                className="flex-1 h-10 rounded-lg border border-slate-700 text-slate-300 text-sm flex items-center justify-center gap-1.5 hover:border-amber-500/40">
                <Pencil className="w-4 h-4" /> Edit
              </button>
              {c.status === "active" ? (
                <button data-testid={`withdraw-contestant-${c.number}`}
                  onClick={() => act(() => api.post(`/contestants/${c.id}/withdraw`), "Withdrawn")}
                  className="flex-1 h-10 rounded-lg border border-red-500/40 text-red-300 text-sm flex items-center justify-center gap-1.5">
                  <UserMinus className="w-4 h-4" /> Withdraw
                </button>
              ) : (
                <button data-testid={`reactivate-contestant-${c.number}`}
                  onClick={() => act(() => api.post(`/contestants/${c.id}/reactivate`), "Reactivated")}
                  className="flex-1 h-10 rounded-lg border border-emerald-500/40 text-emerald-300 text-sm flex items-center justify-center gap-1.5">
                  <UserPlus className="w-4 h-4" /> Reactivate
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing && <ContestantModal contestant={editing === "new" ? null : editing} onClose={() => setEditing(null)} reload={reload} />}
    </div>
  );
}

function ContestantModal({ contestant, onClose, reload }) {
  const [form, setForm] = useState({
    number: contestant?.number ?? "",
    name: contestant?.name ?? "",
    song1_title: contestant?.song1_title ?? "",
    song2_title: contestant?.song2_title ?? "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, number: parseInt(form.number, 10) };
      if (contestant) await api.put(`/contestants/${contestant.id}`, body);
      else await api.post("/contestants", body);
      toast.success("Saved");
      await reload();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <Modal onClose={onClose} title={contestant ? "Edit Contestant" : "Add Contestant"}>
      <div className="space-y-3">
        {[
          { k: "number", label: "Contestant Number", type: "number" },
          { k: "name", label: "Contestant Name", type: "text" },
          { k: "song1_title", label: "Song 1 Title", type: "text" },
          { k: "song2_title", label: "Song 2 Title", type: "text" },
        ].map((f) => (
          <div key={f.k}>
            <label className="block text-sm text-slate-400 mb-1">{f.label}</label>
            <input type={f.type} value={form[f.k]} data-testid={`contestant-${f.k}-input`}
              onChange={(e) => set(f.k, e.target.value)}
              className="w-full h-12 px-3 rounded-xl bg-slate-950 border border-slate-700 text-slate-100 focus:border-amber-500 focus:outline-none" />
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-5">
        <button onClick={onClose} className="flex-1 h-12 rounded-xl border border-slate-700 text-slate-300">Cancel</button>
        <button data-testid="save-contestant-button" onClick={save} disabled={busy || !form.name || form.number === ""}
          className="flex-1 h-12 rounded-xl btn-gold disabled:opacity-40">Save</button>
      </div>
    </Modal>
  );
}

/* ---------------- Audit ---------------- */
function AuditView({ audit }) {
  return (
    <div className="card-kwc p-4 sm:p-5">
      <h3 className="font-display font-bold text-lg text-amber-200 mb-4">Audit Trail</h3>
      <div className="space-y-2" data-testid="audit-list">
        {audit.length === 0 && <p className="text-slate-500 text-sm">No activity yet.</p>}
        {audit.map((a) => (
          <div key={a.id} className="flex items-start gap-3 py-2.5 border-b border-slate-800">
            <div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-slate-200">
                <span className="font-semibold text-amber-300">{a.action.replace(/_/g, " ")}</span>
                {" by "}{a.actor_name}
              </div>
              {a.reason && <div className="text-xs text-red-300 mt-0.5">Reason: {a.reason}</div>}
              <div className="text-[11px] text-slate-500 mt-0.5">{new Date(a.timestamp).toLocaleString()}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Modal shell ---------------- */
function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="card-kwc p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg text-amber-200">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}
